import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { USER_TYPES, PLATFORM, USER_SOURCE } from 'utils/constants/user';
import { useTranslation } from 'utils/modifiedTranslation';
import app from 'new-ui/app';
import useRecaptcha from 'utils/hooks/useRecaptcha';
import { preProcessCondProfile } from 'modules/user/utils';
import { signup } from 'new-ui/functions';
import { RESPONSE_ERROR } from 'utils/constants/responseError';
import { getIntercomUserCondition } from 'modules/intercom/functions';
import { UserSignupForm } from './components/UserSignupForm/UserSignupForm';

const { USER_EXISTS: USER_EXISTS_ERROR_CODE } = RESPONSE_ERROR;

const GA = {
  category: 'HCP',
  events: {
    error: 'Could not signup',
  },
};

export const UserSignup = (props) => {
  const {
    onSignup,
    onBeforeSignup,
    subtitle,
  } = props;

  const [error, setError] = useState(null);
  const { recaptcha, getRecaptchaTokenAsync } = useRecaptcha();

  const { t } = useTranslation();

  const getErrorMessage = (ex) => {
    const { response: { data: { code } = {} } = {} } = ex;
    if (code === USER_EXISTS_ERROR_CODE) {
      return t('add_patient.error.email_duplication');
    }
    return 'Signup Error';
  };

  const handleFormSubmit = async (formData) => {
    const { indication, email, name } = formData ?? {};

    if (!indication?.value || !name || !email) {
      // should be invariant
      setError('error');
      return;
    }

    setError(null);
    app.showPreloader();
    const conditionProfile = await preProcessCondProfile(indication);

    const patient = {
      condition_profile: conditionProfile,
      info: {
        family_name: name,
        email,
        user_type: USER_TYPES.PATIENT,
        platform: PLATFORM.HCP,
      },
      personal: {
        condition: indication.value,
      },
    };

    if (onBeforeSignup) {
      await onBeforeSignup(patient);
    }

    await signup({
      email,
      indication: indication.value,
      recaptchaToken: await getRecaptchaTokenAsync(),
      condition_profile: patient.condition_profile,
      info: patient.info,
      onsignup: async (user) => {
        app.intercom.updateUserAttributes({
          email,
          condition: getIntercomUserCondition(indication),
          user_type: USER_TYPES.PATIENT,
          user_source: USER_SOURCE.HCP,
          shadow_patient: true,
        }, user.info.user_wix_id);

        onSignup(user, setError);
      },
      onerror: (e) => {
        const errorMsg = getErrorMessage(e);
        app.hidePreloader();
        setError(errorMsg);
        app.sendGoogleAnalyticsEvent(GA.category, GA.events.error, { name: 'caregiver_patient_signup_error' });
      },
    });
  };

  return (
    <UserSignupForm
      emailRequired
      onSubmit={handleFormSubmit}
      recaptcha={recaptcha}
      error={error}
      title={t('add_user.title')}
      subtitle={subtitle}
      indicationTitle={t('add_user.indication_input_title')}
      buttonTitle={t('add_user.button_title')}
      nameTitle={t('add_user.name_input_title')}
      emailTitle={t('add_user.email_input_title')}
    />
  );
};

UserSignup.propTypes = {
  onSignup: PropTypes.func.isRequired,
  onBeforeSignup: PropTypes.func.isRequired,
  subtitle: PropTypes.string,
};
